import {
  addDoc,
  collection,
  serverTimestamp,
} from 'firebase/firestore'
import { firestoreDb } from '../firebase'
import { uploadWorkspaceArtifact } from '../storageHubService'
import { enqueueDeferredEvent } from '../deferredEventOutbox'
import { callable } from '../callable'
import {
  deleteBlob,
  deleteMutation,
  getBlob,
  getMutation,
  listMutations,
  listPendingMutations,
  putMutation,
  type ChecklistCommentPayload,
  type ChecklistProofPayload,
  type ChecklistTogglePayload,
  type ChecklistVoiceMemoPayload,
  type HelperMutationRecord,
  type PendingExpenseIntakePayload,
  type StockCountRoundPayload,
  type StockScanSuggestionPayload,
  type WorkspaceActivityPayload,
  type EduBoardingPayload,
  type EduAttendanceMarkPayload,
  type TimeClockPayload,
  type OpsDueRecordPayload,
  type OpsCheckpointScanPayload,
  type OpsSkipDuePayload,
  type OpsRaiseRequestPayload,
  type OpsAttendanceRollPayload,
  type OpsStartRequestPayload,
  type OpsMarkRequestDonePayload,
  type OpsBmwSavePayload,
  type OpsDprSavePayload,
  type OpsConIssueCreatePayload,
} from './helperOutboxDb'
import { refreshHelperQueue } from './queueService'
import {
  getHelperSnapshot,
  recordHelperCompletedMutation,
  setHelperOperationPhase,
  setHelperState,
} from './helperStore'
import { notifyHelper } from './mobileNotificationService'
import { getNlsString } from '../../nls/nlsRuntime'
import { recordWorkspaceActivity } from '../activityService'
import { writeStockCountRound, writeStockScanSuggestion } from '../stockCountSuggestionService'
import { visibilityAwareInterval } from '../../utils/visibilityAwareInterval'
import {
  isTimeClockServerRejection,
  extractCallableErrorCode,
} from '../timesheetsService'

export const isOnline = (): boolean => typeof navigator === 'undefined' || navigator.onLine !== false

const POLL_MS = 30_000
const MAX_RETRIES = 3
const CONFLICT_ERROR = 'CONFLICT_NEWER_SERVER'
const HELPER_HIDDEN_ERROR = 'CHECKLIST_HIDDEN_FROM_HELPER'

let pollTimer: (() => void) | null = null
let onlineHandler: (() => void) | null = null
let offlineHandler: (() => void) | null = null
let inflight = false

export const toMillis = (value: unknown): number => {
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  if (typeof value === 'number') return value
  if (typeof value === 'object' && value && 'toMillis' in value) {
    const fn = (value as { toMillis: () => number }).toMillis
    if (typeof fn === 'function') return fn.call(value)
  }
  return 0
}

export const conflictAgainst = (assignedAtIso: string | null, serverUpdatedAt: unknown): boolean => {
  if (!assignedAtIso) return false
  const assignedAtMs = Date.parse(assignedAtIso)
  if (!Number.isFinite(assignedAtMs)) return false
  return toMillis(serverUpdatedAt) > assignedAtMs
}

export const assertHelperCanAccessItem = (
  itemData: Record<string, unknown>,
  actorRole: 'HELPER' | 'MEMBER' | undefined,
): void => {
  // Member/admin writes bypass helper-only visibility masking. The mask exists
  // to hide items from the HELPER surface — a MEMBER acting on the same item
  // through chat quick actions must never be blocked by it.
  if (actorRole === 'MEMBER') return
  if (itemData.isHelperVisible === false) throw new Error(HELPER_HIDDEN_ERROR)
}

export type HelperReplayFailureDecision = {
  retryCount: number
  shouldFail: boolean
  status: HelperMutationRecord['status']
  isConflict: boolean
  isHidden: boolean
  isPermissionDenied?: boolean
  notificationTitle: string
  notificationBody: string
}

export const classifyHelperReplayFailure = (
  message: string,
  currentRetryCount: number,
  options?: string | { workspaceName?: string; isMember?: boolean; errorCode?: string },
): HelperReplayFailureDecision => {
  const errorCode = typeof options === 'object' ? options?.errorCode : undefined
  const isDataMissing = message.includes('CHECKLIST_MISSING')
    || message.includes('UPLOAD_MISSING')
    || message.includes('WORKSPACE_MISSING')
    || message.includes('RECORD_MISSING')
  const isConflict = !isDataMissing && (message === CONFLICT_ERROR
    || message.includes('CONFLICT_NEWER_SERVER')
    || (errorCode === 'failed-precondition' && message.includes('CONFLICT')))
  let isHidden = !isDataMissing && (message === HELPER_HIDDEN_ERROR || message.includes('CHECKLIST_HIDDEN_FROM_HELPER'))
  const isPermissionDenied = !isDataMissing && (/permission-denied|permission denied|insufficient permissions|PHONE_ITEM_NOT_ALLOWED/i.test(message)
    || errorCode === 'permission-denied')
  const isBlocker = !isDataMissing && (/action blocked|blocker|dependency|prerequisite|location check-in|proof required/i.test(message)
    || message.includes('DEPENDENCY_INCOMPLETE'))
  const workspaceName = typeof options === 'string' ? options : options?.workspaceName
  const isMember = typeof options === 'object' && typeof options?.isMember === 'boolean' ? options.isMember : undefined

  // Permission-denied is terminal with "no longer in {name}" ONLY when workspace is no longer in list/active
  const isRemoved = isPermissionDenied && isMember === false
  if (isPermissionDenied && !isRemoved) {
    // Otherwise it takes the existing conflict/hidden path
    isHidden = true
  }

  // Only the wrapper's missing-function error keeps the record PENDING (citing src/services/callable.ts:164)
  const isNotFound = !isDataMissing && (errorCode === 'not-found' || errorCode === 'unimplemented')

  const retryCount = isNotFound ? currentRetryCount : currentRetryCount + 1
  const shouldFail = !isNotFound && (isDataMissing || isConflict || isHidden || isRemoved || isBlocker || retryCount >= MAX_RETRIES)
  const fallbackWorkspace = getNlsString('NLSWorkspaceFallbackThisWorkspace')
  const targetName = workspaceName || fallbackWorkspace
  const accessLostText = getNlsString('NLSMutationAccessLost', { name: targetName })
  return {
    retryCount,
    shouldFail,
    status: isRemoved ? ('ACCESS_LOST' as const) : shouldFail ? ('FAILED' as const) : ('PENDING' as const),
    isConflict,
    isHidden,
    isPermissionDenied,
    notificationTitle: isDataMissing
      ? 'Task is no longer available'
      : isConflict
        ? 'Update failed: admin changed this task'
        : isHidden
          ? 'Task is no longer available'
          : isRemoved
            ? accessLostText
            : isBlocker
              ? 'Action blocked'
              : 'Helper sync failed',
    notificationBody: isDataMissing
      ? 'The task was removed while you were offline.'
      : isConflict
        ? 'The task was updated by an admin while you were offline. Tap Retry or Reset to Baseline.'
        : isHidden
          ? 'An admin hid this task from helpers. The pending change was not sent.'
          : isRemoved
            ? accessLostText
            : isBlocker
              ? message
              : 'A pending change could not be sent. Open the Helper panel to retry.',
  }
}

const writeOutboxLog = async (
  workspaceId: string,
  record: HelperMutationRecord,
  outcome: 'APPLIED' | 'FAILED',
  errorMessage: string | null,
): Promise<void> => {
  try {
    await addDoc(collection(firestoreDb, 'workspaces', workspaceId, 'outbox'), {
      mutationId: record.id,
      type: record.type,
      outcome,
      error: errorMessage,
      clientCreatedAt: record.createdAt,
      serverWrittenAt: serverTimestamp(),
    })
  } catch {
    // Best-effort audit log; never block the primary mutation on this.
  }
}

const recordMobileSyncDiagnostic = async (
  workspaceId: string,
  record: HelperMutationRecord,
  errorMessage: string,
): Promise<void> => {
  const payload = record.payload as {
    actorId?: string
    actorName?: string
    checklistItemId?: string
    fileName?: string
  }
  await enqueueDeferredEvent({
    actionKey: 'WORKSPACE_ACTIVITY',
    kind: 'WORKSPACE_ACTIVITY',
    payload: {
      workspaceId,
      actorId: payload.actorId ?? 'unknown',
      actorName: payload.actorName ?? 'Mobile user',
      targetType: 'CHECKLIST',
      targetId: payload.checklistItemId ?? record.id,
      targetTitle: payload.fileName ?? 'Mobile sync item',
      action: 'MOBILE_SYNC_RETRY_NEEDED',
      summary: 'A mobile offline update needs attention before it can sync.',
      metadata: {
        mutationId: record.id,
        mutationType: record.type,
        retryCount: record.retryCount,
        error: errorMessage,
      },
    },
  }).catch(() => false)
}

export const applyChecklistToggle = async (
  payload: ChecklistTogglePayload,
  mutationId?: string,
): Promise<void> => {
  const clientMutationId = mutationId
  if (!clientMutationId) {
    throw new Error('clientMutationId is required for outbox replay')
  }
  await callable(
    'helperToggleChecklistItem',
    {
      clientMutationId,
      workspaceId: payload.workspaceId,
      checklistItemId: payload.checklistItemId,
      isCompleted: payload.isCompleted,
      workflowStatus: payload.workflowStatus,
      pageKey: payload.pageKey,
      assignedAt: payload.assignedAt,
      actorId: payload.actorId,
      actorRole: payload.actorRole,
    },
    { idempotencyKey: clientMutationId },
  )
}

export const applyChecklistProof = async (
  payload: ChecklistProofPayload,
  mutationId?: string,
): Promise<void> => {
  const clientMutationId = mutationId
  if (!clientMutationId) {
    throw new Error('clientMutationId is required for outbox replay')
  }
  let fileId = payload.fileId
  if (!fileId) {
    const blobRecord = await getBlob(payload.blobKey)
    if (!blobRecord) throw new Error('Local proof blob missing')
    const file = new File([blobRecord.blob], payload.fileName, { type: payload.contentType })
    setHelperOperationPhase(clientMutationId, 'UPLOADING')
    const uploaded = await uploadWorkspaceArtifact({
      workspaceId: payload.workspaceId,
      file,
      artifactType: 'HELPER_CHECKLIST_PROOF',
      artifactId: payload.checklistItemId,
    })
    fileId = uploaded.fileId
    const record = await getMutation(clientMutationId)
    if (record) {
      await putMutation({
        ...record,
        payload: {
          ...(record.payload as ChecklistProofPayload),
          fileId,
        },
      })
    }
  }

  setHelperOperationPhase(clientMutationId, 'FINALIZING')
  await callable(
    'helperSubmitChecklistProof',
    {
      clientMutationId,
      workspaceId: payload.workspaceId,
      checklistItemId: payload.checklistItemId,
      fileId,
      fileName: payload.fileName,
      contentType: payload.contentType,
      pageKey: payload.pageKey,
      assignedAt: payload.assignedAt,
      actorId: payload.actorId,
      actorRole: payload.actorRole,
    },
    { idempotencyKey: clientMutationId },
  )
  await deleteBlob(payload.blobKey)
}

export const applyChecklistComment = async (
  payload: ChecklistCommentPayload,
  mutationId?: string,
): Promise<void> => {
  const clientMutationId = mutationId
  if (!clientMutationId) {
    throw new Error('clientMutationId is required for outbox replay')
  }
  await callable(
    'helperAddChecklistComment',
    {
      clientMutationId,
      workspaceId: payload.workspaceId,
      checklistItemId: payload.checklistItemId,
      parentContainerId: payload.parentContainerId,
      body: payload.body,
      pageKey: payload.pageKey,
      assignedAt: payload.assignedAt,
      actorId: payload.actorId,
      actorRole: payload.actorRole,
    },
    { idempotencyKey: clientMutationId },
  )
}

export const applyChecklistVoiceMemo = async (
  payload: ChecklistVoiceMemoPayload,
  mutationId?: string,
): Promise<void> => {
  const clientMutationId = mutationId
  if (!clientMutationId) {
    throw new Error('clientMutationId is required for outbox replay')
  }
  let fileId = payload.fileId
  if (!fileId) {
    const blobRecord = await getBlob(payload.blobKey)
    if (!blobRecord) throw new Error('Local voice memo blob missing')
    const file = new File([blobRecord.blob], `memo-${payload.checklistItemId}.webm`, { type: 'audio/webm' })
    setHelperOperationPhase(clientMutationId, 'UPLOADING')
    const uploaded = await uploadWorkspaceArtifact({
      workspaceId: payload.workspaceId,
      file,
      artifactType: 'HELPER_CHECKLIST_VOICE_MEMO',
      artifactId: payload.checklistItemId,
    })
    fileId = uploaded.fileId
    const record = await getMutation(clientMutationId)
    if (record) {
      await putMutation({
        ...record,
        payload: {
          ...(record.payload as ChecklistVoiceMemoPayload),
          fileId,
        },
      })
    }
  }

  setHelperOperationPhase(clientMutationId, 'FINALIZING')
  await callable(
    'helperAttachChecklistVoiceMemo',
    {
      clientMutationId,
      workspaceId: payload.workspaceId,
      checklistItemId: payload.checklistItemId,
      fileId,
      durationMs: payload.durationMs,
      pageKey: payload.pageKey,
      assignedAt: payload.assignedAt,
      actorId: payload.actorId,
      actorRole: payload.actorRole,
    },
    { idempotencyKey: clientMutationId },
  )
  await deleteBlob(payload.blobKey)
}

export const applyWorkspaceActivity = async (payload: WorkspaceActivityPayload): Promise<void> => {
  await recordWorkspaceActivity(firestoreDb, {
    workspaceId: payload.workspaceId,
    actorId: payload.actorId,
    actorName: payload.actorName,
    targetType: payload.targetType,
    targetId: payload.targetId,
    targetTitle: payload.targetTitle,
    action: payload.action,
    summary: payload.summary,
    metadata: payload.metadata,
  })
}

export const applyStockCountRound = async (payload: StockCountRoundPayload): Promise<void> => {
  await writeStockCountRound(payload, firestoreDb)
}

export const applyStockScanSuggestion = async (payload: StockScanSuggestionPayload): Promise<void> => {
  await writeStockScanSuggestion(payload, firestoreDb)
}

export const applyPendingExpenseIntake = async (
  payload: PendingExpenseIntakePayload,
  mutationId?: string,
): Promise<void> => {
  const clientMutationId = mutationId
  if (!clientMutationId) {
    throw new Error('clientMutationId is required for outbox replay')
  }
  let fileId = payload.fileId
  if (!fileId) {
    const blobRecord = await getBlob(payload.blobKey)
    if (!blobRecord) throw new Error('Local receipt blob missing')
    const file = new File([blobRecord.blob], payload.fileName, { type: payload.contentType })
    setHelperOperationPhase(clientMutationId, 'UPLOADING')
    const uploaded = await uploadWorkspaceArtifact({
      workspaceId: payload.workspaceId,
      file,
      artifactType: 'HELPER_PENDING_EXPENSE_RECEIPT',
      artifactId: payload.checklistItemId,
    })
    fileId = uploaded.fileId
    const record = await getMutation(clientMutationId)
    if (record) {
      await putMutation({
        ...record,
        payload: {
          ...(record.payload as PendingExpenseIntakePayload),
          fileId,
        },
      })
    }
  }

  setHelperOperationPhase(clientMutationId, 'FINALIZING')
  await callable(
    'helperSubmitPendingExpenseIntake',
    {
      clientMutationId,
      workspaceId: payload.workspaceId,
      checklistItemId: payload.checklistItemId,
      fileId,
      amountMinor: payload.amountMinor,
      currencyCode: payload.currencyCode,
      category: payload.category,
      note: payload.note,
      taskId: payload.taskId,
      assignedAt: payload.assignedAt,
      actorId: payload.actorId,
      actorRole: payload.actorRole,
    },
    { idempotencyKey: clientMutationId },
  )
  await deleteBlob(payload.blobKey)
}

export const applyEduBoarding = async (payload: EduBoardingPayload): Promise<void> => {
  const { markBoarding } = await import('../eduHelperJobsService')
  await markBoarding({
    workspaceId: payload.workspaceId,
    runId: payload.runId,
    learnerId: payload.learnerId,
    status: payload.status,
    clientMutationId: payload.clientMutationId,
  })
}

export const applyEduAttendanceMark = async (payload: EduAttendanceMarkPayload): Promise<void> => {
  const { markEduAttendance } = await import('../eduAttendanceService')
  await markEduAttendance({
    workspaceId: payload.workspaceId,
    seriesId: payload.seriesId,
    date: payload.date,
    marks: payload.marks,
    reason: payload.reason,
  })
}

export const applyTimeClock = async (payload: TimeClockPayload): Promise<void> => {
  const { startClock, stopClock } = await import('../timesheetsService')
  if (payload.action === 'START') {
    await startClock({
      workspaceId: payload.workspaceId,
      clientGeneratedId: payload.clientGeneratedId,
      kind: payload.kind ?? 'CLOCK',
      nodeId: payload.nodeId ?? null,
      geoIn: payload.geo ?? null,
      device: payload.device ?? null,
      occurredAt: payload.occurredAt,
    })
  } else {
    await stopClock({
      workspaceId: payload.workspaceId,
      clientGeneratedId: payload.clientGeneratedId,
      geoOut: payload.geo ?? null,
      occurredAt: payload.occurredAt,
    })
  }
}

export const applyOpsDueRecord = async (payload: OpsDueRecordPayload, recordId: string): Promise<void> => {
  const { submitRecord } = await import('../opsRegistersService')
  await submitRecord({
    ...payload,
    opId: payload.opId || payload.clientMutationId || recordId,
  })
}

export const applyOpsCheckpointScan = async (payload: OpsCheckpointScanPayload, recordId: string): Promise<void> => {
  const { recordCheckpointScan } = await import('../opsSchedulesService')
  await recordCheckpointScan({
    ...payload,
    opId: payload.opId || payload.clientMutationId || recordId,
  })
}

export const applyOpsSkipDue = async (payload: OpsSkipDuePayload, recordId: string): Promise<void> => {
  const { skipDue } = await import('../opsSchedulesService')
  await skipDue({
    ...payload,
    opId: payload.opId || payload.clientMutationId || recordId,
  })
}

export const applyOpsRaiseRequest = async (payload: OpsRaiseRequestPayload, recordId: string): Promise<void> => {
  const { raiseRequest } = await import('../opsRequestsService')
  await raiseRequest({
    ...payload,
    opId: payload.opId || payload.clientMutationId || recordId,
  })
}

export const applyOpsAttendanceRoll = async (payload: OpsAttendanceRollPayload, recordId: string): Promise<void> => {
  const { markAttendanceRoll } = await import('../opsRostersService')
  await markAttendanceRoll({
    ...payload,
    opId: payload.opId || payload.clientMutationId || recordId,
  })
}

export const applyOpsStartRequest = async (payload: OpsStartRequestPayload, recordId: string): Promise<void> => {
  const { startRequest } = await import('../opsRequestsService')
  await startRequest({
    ...payload,
    opId: payload.opId || payload.clientMutationId || recordId,
  })
}

export const applyOpsMarkRequestDone = async (payload: OpsMarkRequestDonePayload, recordId: string): Promise<void> => {
  const { markRequestDone } = await import('../opsRequestsService')
  const { uploadRequestPhoto } = await import('../opsRequestPhotosService')

  const keysToProcess: string[] = []
  if (Array.isArray(payload.blobKeys) && payload.blobKeys.length > 0) {
    keysToProcess.push(...payload.blobKeys)
  } else if (payload.blobKey) {
    keysToProcess.push(payload.blobKey)
  }

  const photos = payload.photos ? [...payload.photos] : []

  for (let i = photos.length; i < keysToProcess.length; i++) {
    const bKey = keysToProcess[i]
    const blobRecord = await getBlob(bKey)
    if (!blobRecord) {
      throw new Error(`Local request photo blob missing: ${bKey}`)
    }
    setHelperOperationPhase(recordId, 'UPLOADING')
    const file = new File([blobRecord.blob], `photo-${bKey}.jpg`, {
      type: blobRecord.contentType || 'image/jpeg',
    })
    const uploaded = await uploadRequestPhoto(payload.workspaceId, payload.requestId, file)
    photos.push(uploaded.path)
    payload.photos = [...photos]
    try {
      const currentRec = await getMutation(recordId)
      if (currentRec) {
        await putMutation({
          ...currentRec,
          payload: {
            ...(currentRec.payload as OpsMarkRequestDonePayload),
            photos: [...photos],
          },
        })
      }
    } catch (err) {
      console.warn('[syncWorker] Failed to persist uploaded request photos to outbox mutation:', err)
    }
  }

  setHelperOperationPhase(recordId, 'FINALIZING')
  await markRequestDone({
    workspaceId: payload.workspaceId,
    requestId: payload.requestId,
    ...(photos.length > 0 ? { photos } : {}),
    opId: payload.opId || payload.clientMutationId || recordId,
  })

  for (const bKey of keysToProcess) {
    await deleteBlob(bKey)
  }
}

export const applyOpsBmwSave = async (payload: OpsBmwSavePayload, recordId: string): Promise<void> => {
  const { saveBmwDay } = await import('../hosBmwService')
  await saveBmwDay({
    workspaceId: payload.workspaceId,
    placeId: payload.placeId,
    dayKey: payload.dayKey,
    weights: payload.weights,
    bagCounts: payload.bagCounts,
    opId: payload.opId || payload.clientMutationId || recordId,
  })
}

export const applyOpsDprSave = async (payload: OpsDprSavePayload, recordId: string): Promise<void> => {
  const { saveDprDraft } = await import('../conDprService')
  const { buildConDprRecordId } = await import('../../types/ops')

  const dprId = buildConDprRecordId(payload.projectId, payload.reportDate, payload.shift)

  const lines = payload.lines ? payload.lines.map((l) => ({ ...l, photoPaths: [...(l.photoPaths || [])] })) : []
  let lineBlobs = (payload.lineBlobs || []).map((lb) => ({
    lineId: lb.lineId,
    blobKeys: Array.isArray(lb.blobKeys) ? [...lb.blobKeys] : lb.blobKey ? [lb.blobKey] : [],
  }))

  const processedBlobKeys: string[] = []
  for (const lb of payload.lineBlobs || []) {
    const bKeys = Array.isArray(lb.blobKeys) ? lb.blobKeys : lb.blobKey ? [lb.blobKey] : []
    for (const bKey of bKeys) {
      const blobRecord = await getBlob(bKey)
      if (!blobRecord) {
        throw new Error(`Local DPR line photo blob missing: ${bKey}`)
      }
      setHelperOperationPhase(recordId, 'UPLOADING')
      const file = new File([blobRecord.blob], `dpr-photo-${bKey}.jpg`, {
        type: blobRecord.contentType || 'image/jpeg',
      })
      const uploaded = await uploadWorkspaceArtifact({
        workspaceId: payload.workspaceId,
        file,
        artifactType: 'OPS_RECORD_PHOTO',
        artifactId: dprId,
      })
      const storagePath =
        (uploaded as { storagePath?: string; path?: string }).storagePath ||
        (uploaded as { storagePath?: string; path?: string }).path ||
        ''
      if (!storagePath || !storagePath.trim()) {
        throw new Error('Upload returned no storage path')
      }

      const line = lines.find((l) => l.lineId === lb.lineId)
      if (line) {
        line.photoPaths = line.photoPaths || []
        if (!line.photoPaths.includes(storagePath)) {
          line.photoPaths.push(storagePath)
        }
      }
      processedBlobKeys.push(bKey)

      lineBlobs = lineBlobs.map((entry) => {
        if (entry.lineId !== lb.lineId) return entry
        return {
          ...entry,
          blobKeys: entry.blobKeys.filter((k) => k !== bKey),
        }
      })

      try {
        const currentRec = await getMutation(recordId)
        if (currentRec) {
          await putMutation({
            ...currentRec,
            payload: {
              ...(currentRec.payload as OpsDprSavePayload),
              lines,
              lineBlobs,
            },
          })
        }
      } catch (err) {
        console.warn('[syncWorker] Failed to persist uploaded DPR line photos to outbox mutation:', err)
      }
    }
  }

  setHelperOperationPhase(recordId, 'FINALIZING')
  await saveDprDraft({
    workspaceId: payload.workspaceId,
    projectId: payload.projectId,
    reportDate: payload.reportDate,
    shift: payload.shift,
    lines,
    weather: payload.weather,
    safetyExceptions: payload.safetyExceptions,
    safetyNotes: payload.safetyNotes,
    keyReceiptsNotes: payload.keyReceiptsNotes,
    inspectionRefsNotes: payload.inspectionRefsNotes,
    opId: payload.opId || payload.clientMutationId || recordId,
  })

  for (const bKey of processedBlobKeys) {
    await deleteBlob(bKey)
  }
}

export const applyOpsConIssueCreate = async (payload: OpsConIssueCreatePayload, recordId: string): Promise<void> => {
  const { createConIssue, attachConIssuePhotos } = await import('../conIssuesService')
  const { uploadConIssuePhoto } = await import('../conIssuePhotosService')

  const opId = payload.opId || payload.clientMutationId || recordId
  let issueId = payload.createdIssueId

  // 1. Create issue if not already created on an earlier attempt
  if (!issueId) {
    setHelperOperationPhase(recordId, 'SYNCING')
    const createRes = await createConIssue({
      workspaceId: payload.workspaceId,
      projectId: payload.projectId,
      placeId: payload.placeId,
      kind: payload.kind,
      title: payload.title,
      description: payload.description,
      firstNoticedAt: payload.firstNoticedAt,
      responsibleParty: payload.responsibleParty,
      responseDueAt: payload.responseDueAt,
      impact: payload.impact,
      drawingRef: payload.drawingRef,
      contractNoticeRef: payload.contractNoticeRef,
      options: payload.options,
      opId,
    })
    issueId = createRes.issueId
    payload.createdIssueId = issueId

    try {
      const currentRec = await getMutation(recordId)
      if (currentRec) {
        await putMutation({
          ...currentRec,
          payload: {
            ...(currentRec.payload as OpsConIssueCreatePayload),
            createdIssueId: issueId,
          },
        })
      }
    } catch (err) {
      console.warn('[syncWorker] Failed to persist createdIssueId to outbox mutation:', err)
    }
  }

  // 2. Upload remaining blobs while persisting progress
  const keysToProcess = Array.isArray(payload.blobKeys) ? payload.blobKeys : []
  const uploadedPhotoPaths = payload.uploadedPhotoPaths ? [...payload.uploadedPhotoPaths] : []

  for (let i = uploadedPhotoPaths.length; i < keysToProcess.length; i++) {
    const bKey = keysToProcess[i]
    const blobRecord = await getBlob(bKey)
    if (!blobRecord) {
      throw new Error(`Local hindrance photo blob missing: ${bKey}`)
    }
    setHelperOperationPhase(recordId, 'UPLOADING')
    const file = new File([blobRecord.blob], `hindrance-photo-${bKey}.jpg`, {
      type: blobRecord.contentType || 'image/jpeg',
    })
    const uploaded = await uploadConIssuePhoto(payload.workspaceId, issueId, file)
    uploadedPhotoPaths.push(uploaded.path)
    payload.uploadedPhotoPaths = [...uploadedPhotoPaths]

    try {
      const currentRec = await getMutation(recordId)
      if (currentRec) {
        await putMutation({
          ...currentRec,
          payload: {
            ...(currentRec.payload as OpsConIssueCreatePayload),
            uploadedPhotoPaths: [...uploadedPhotoPaths],
          },
        })
      }
    } catch (err) {
      console.warn('[syncWorker] Failed to persist uploaded hindrance photo paths to outbox mutation:', err)
    }
  }

  // 3. Attach photos if any
  if (uploadedPhotoPaths.length > 0) {
    setHelperOperationPhase(recordId, 'FINALIZING')
    await attachConIssuePhotos({
      workspaceId: payload.workspaceId,
      issueId,
      photoPaths: uploadedPhotoPaths,
      opId: `${opId}_photos`,
    })
  }

  // 4. Delete local blobs
  for (const bKey of keysToProcess) {
    await deleteBlob(bKey)
  }
}

const applyRecord = async (record: HelperMutationRecord): Promise<void> => {
  switch (record.type) {
    case 'CHECKLIST_TOGGLE':
      setHelperOperationPhase(record.id, 'SYNCING')
      return applyChecklistToggle(record.payload as ChecklistTogglePayload, record.id)
    case 'CHECKLIST_PROOF_UPLOAD':
      return applyChecklistProof(record.payload as ChecklistProofPayload, record.id)
    case 'CHECKLIST_COMMENT':
      setHelperOperationPhase(record.id, 'SYNCING')
      return applyChecklistComment(record.payload as ChecklistCommentPayload, record.id)
    case 'CHECKLIST_VOICE_MEMO':
      return applyChecklistVoiceMemo(record.payload as ChecklistVoiceMemoPayload, record.id)
    case 'WORKSPACE_ACTIVITY':
      setHelperOperationPhase(record.id, 'SYNCING')
      return applyWorkspaceActivity(record.payload as WorkspaceActivityPayload)
    case 'STOCK_COUNT_ROUND':
      setHelperOperationPhase(record.id, 'SYNCING')
      return applyStockCountRound(record.payload as StockCountRoundPayload)
    case 'STOCK_SCAN_SUGGESTION':
      setHelperOperationPhase(record.id, 'SYNCING')
      return applyStockScanSuggestion(record.payload as StockScanSuggestionPayload)
    case 'PENDING_EXPENSE_INTAKE':
      return applyPendingExpenseIntake(record.payload as PendingExpenseIntakePayload, record.id)
    case 'EDU_BOARDING':
      setHelperOperationPhase(record.id, 'SYNCING')
      return applyEduBoarding(record.payload as EduBoardingPayload)
    case 'EDU_ATTENDANCE_MARK':
      setHelperOperationPhase(record.id, 'SYNCING')
      return applyEduAttendanceMark(record.payload as EduAttendanceMarkPayload)
    case 'TIME_CLOCK':
      setHelperOperationPhase(record.id, 'SYNCING')
      return applyTimeClock(record.payload as TimeClockPayload)
    case 'OPS_DUE_RECORD':
      setHelperOperationPhase(record.id, 'SYNCING')
      return applyOpsDueRecord(record.payload as OpsDueRecordPayload, record.id)
    case 'OPS_CHECKPOINT_SCAN':
      setHelperOperationPhase(record.id, 'SYNCING')
      return applyOpsCheckpointScan(record.payload as OpsCheckpointScanPayload, record.id)
    case 'OPS_SKIP_DUE':
      setHelperOperationPhase(record.id, 'SYNCING')
      return applyOpsSkipDue(record.payload as OpsSkipDuePayload, record.id)
    case 'OPS_RAISE_REQUEST':
      setHelperOperationPhase(record.id, 'SYNCING')
      return applyOpsRaiseRequest(record.payload as OpsRaiseRequestPayload, record.id)
    case 'OPS_ATTENDANCE_ROLL':
      setHelperOperationPhase(record.id, 'SYNCING')
      return applyOpsAttendanceRoll(record.payload as OpsAttendanceRollPayload, record.id)
    case 'OPS_START_REQUEST':
      setHelperOperationPhase(record.id, 'SYNCING')
      return applyOpsStartRequest(record.payload as OpsStartRequestPayload, record.id)
    case 'OPS_MARK_REQUEST_DONE':
      setHelperOperationPhase(record.id, 'SYNCING')
      return applyOpsMarkRequestDone(record.payload as OpsMarkRequestDonePayload, record.id)
    case 'OPS_BMW_SAVE':
      setHelperOperationPhase(record.id, 'SYNCING')
      return applyOpsBmwSave(record.payload as OpsBmwSavePayload, record.id)
    case 'OPS_DPR_SAVE':
      setHelperOperationPhase(record.id, 'SYNCING')
      return applyOpsDprSave(record.payload as OpsDprSavePayload, record.id)
    case 'OPS_CON_ISSUE_CREATE':
      setHelperOperationPhase(record.id, 'SYNCING')
      return applyOpsConIssueCreate(record.payload as OpsConIssueCreatePayload, record.id)
    case 'OPS_CON_ISSUE_ATTACH_PHOTOS':
      setHelperOperationPhase(record.id, 'FINALIZING')
      return
    default: {
      const exhaustive: never = record.type
      throw new Error(`Unknown helper mutation type: ${exhaustive as string}`)
    }
  }
}

export type MutationTerminalOutcome = { status: 'APPLIED' } | { status: 'FAILED'; error: string }

const terminalWaiters = new Map<string, Array<(outcome: MutationTerminalOutcome) => void>>()
const recentTerminalOutcomes = new Map<string, MutationTerminalOutcome>()

export const waitForMutationTerminalState = (
  mutationId: string,
  timeoutMs = 15000,
): Promise<MutationTerminalOutcome> => {
  if (recentTerminalOutcomes.has(mutationId)) {
    return Promise.resolve(recentTerminalOutcomes.get(mutationId)!)
  }
  return new Promise((resolve) => {
    let timer: ReturnType<typeof setTimeout> | null = null

    const onOutcome = (outcome: MutationTerminalOutcome) => {
      if (timer) clearTimeout(timer)
      resolve(outcome)
    }

    const current = terminalWaiters.get(mutationId) ?? []
    current.push(onOutcome)
    terminalWaiters.set(mutationId, current)

    timer = setTimeout(() => {
      const remaining = (terminalWaiters.get(mutationId) ?? []).filter((fn) => fn !== onOutcome)
      if (remaining.length > 0) {
        terminalWaiters.set(mutationId, remaining)
      } else {
        terminalWaiters.delete(mutationId)
      }
      resolve({ status: 'FAILED', error: 'Mutation timed out while syncing' })
    }, timeoutMs)
  })
}

export const notifyMutationTerminalState = (mutationId: string, outcome: MutationTerminalOutcome): void => {
  recentTerminalOutcomes.set(mutationId, outcome)
  if (recentTerminalOutcomes.size > 100) {
    const oldest = recentTerminalOutcomes.keys().next().value
    if (oldest) recentTerminalOutcomes.delete(oldest)
  }
  const waiters = terminalWaiters.get(mutationId)
  if (waiters) {
    terminalWaiters.delete(mutationId)
    waiters.forEach((fn) => fn(outcome))
  }
}

export const drainOutbox = async (): Promise<void> => {
  if (inflight) return
  if (!isOnline()) {
    setHelperState({ syncState: 'OFFLINE' })
    return
  }
  inflight = true
  setHelperState({ syncState: 'SYNCING', lastError: null })
  try {
    while (true) {
      const pending = await listPendingMutations()
      if (pending.length === 0) break
      for (const entry of pending) {
        // Enforce queue order for TIME_CLOCK:
        // a STOP queued after its START replays only after the START completed (keep queue order per workspace+uid).
        if (entry.type === 'TIME_CLOCK') {
          const tcPayload = entry.payload as TimeClockPayload
          if (tcPayload.action === 'STOP') {
            const allMutations = await listMutations()
            const priorStart = allMutations.find(
              (m) =>
                m.id !== entry.id &&
                m.type === 'TIME_CLOCK' &&
                (m.payload as TimeClockPayload).workspaceId === tcPayload.workspaceId &&
                ((m.payload as TimeClockPayload).actorId ?? '') === (tcPayload.actorId ?? '') &&
                (m.payload as TimeClockPayload).action === 'START' &&
                m.createdAt < entry.createdAt,
            )
            if (priorStart) {
              if (priorStart.status === 'FAILED') {
                const errorMsg = `Previous clock start failed to sync: ${priorStart.error ?? 'Rejected'}`
                const failedStop: HelperMutationRecord = {
                  ...entry,
                  status: 'FAILED',
                  error: errorMsg,
                  lastUpdatedClient: Date.now(),
                }
                await putMutation(failedStop)
                notifyMutationTerminalState(entry.id, { status: 'FAILED', error: errorMsg })
                continue
              } else {
                // START is still pending or syncing: STOP replays only after START completes
                continue
              }
            }
          }
        }

        const workspaceId = (entry.payload as { workspaceId?: string }).workspaceId ?? ''
        const workspaceName = (entry.payload as { workspaceName?: string }).workspaceName
        const syncing: HelperMutationRecord = { ...entry, status: 'SYNCING' }
        await putMutation(syncing)
        try {
          await applyRecord(syncing)
          await writeOutboxLog(workspaceId, syncing, 'APPLIED', null)
          recordHelperCompletedMutation(syncing)
          setHelperOperationPhase(syncing.id, null)
          await deleteMutation(syncing.id)
          notifyMutationTerminalState(syncing.id, { status: 'APPLIED' })
        } catch (rawError) {
          setHelperOperationPhase(syncing.id, null)
          const message = rawError instanceof Error ? rawError.message : 'Unknown error'
          const errorCode = extractCallableErrorCode(rawError) || (rawError as { code?: string })?.code
          const helperSnap = getHelperSnapshot()
          const ws = workspaceId ? helperSnap.workspaces.find((w) => w.id === workspaceId) : null
          const isMember = Boolean(ws && (!ws.role || ws.role !== 'INACTIVE'))
          const decision = classifyHelperReplayFailure(message, syncing.retryCount, {
            workspaceName,
            isMember,
            errorCode,
          })

          // TIME_CLOCK: server rejection marks record FAILED with callable's message (no retry loop).
          // Transient/network failures, unavailable, internal, etc. stay retryable via normal classifyHelperReplayFailure path.
          if (syncing.type === 'TIME_CLOCK' && isTimeClockServerRejection(rawError)) {
            decision.status = 'FAILED'
            decision.shouldFail = true
          }

          const updated: HelperMutationRecord = {
            ...syncing,
            status: decision.status,
            retryCount: decision.retryCount,
            error: decision.status === 'ACCESS_LOST' ? decision.notificationTitle : message,
            lastUpdatedClient: Date.now(),
          }
          await putMutation(updated)
          if (decision.status === 'FAILED' || decision.status === 'ACCESS_LOST') {
            notifyMutationTerminalState(syncing.id, {
              status: 'FAILED',
              error: decision.status === 'ACCESS_LOST' ? decision.notificationTitle : message,
            })
          }
          if (decision.shouldFail) {
            await writeOutboxLog(workspaceId, updated, 'FAILED', message)
            await recordMobileSyncDiagnostic(workspaceId, updated, message)
            notifyHelper({
              title: decision.notificationTitle,
              body: decision.notificationBody,
              tag: `helper-mutation-${updated.id}`,
            })
          }
        }
      }
    }
    await refreshHelperQueue()
    setHelperState({ syncState: isOnline() ? 'IDLE' : 'OFFLINE', lastSyncAt: Date.now() })
  } catch (rawError) {
    const message = rawError instanceof Error ? rawError.message : 'Sync failed'
    setHelperState({ syncState: 'ERROR', lastError: message })
  } finally {
    inflight = false
  }
}

export const retryMutation = async (id: string): Promise<void> => {
  const pending = await listPendingMutations()
  const target = pending.find((entry) => entry.id === id)
  if (target) {
    await putMutation({ ...target, status: 'PENDING', error: null, retryCount: 0 })
  } else {
    // Failed records aren't in listPendingMutations; refetch full list.
    const records = await (await import('./helperOutboxDb')).listMutations()
    const failed = records.find((entry) => entry.id === id)
    if (failed) {
      await putMutation({ ...failed, status: 'PENDING', error: null, retryCount: 0 })
    }
  }
  await refreshHelperQueue()
  void drainOutbox()
}

export const startSyncWorker = (): void => {
  if (pollTimer) return
  void drainOutbox()
  pollTimer = visibilityAwareInterval(() => {
    void drainOutbox()
  }, POLL_MS)
  if (typeof window !== 'undefined') {
    onlineHandler = () => {
      setHelperState({ syncState: 'IDLE' })
      void drainOutbox()
    }
    offlineHandler = () => {
      setHelperState({ syncState: 'OFFLINE' })
    }
    window.addEventListener('online', onlineHandler)
    window.addEventListener('offline', offlineHandler)
  }
}

export const stopSyncWorker = (): void => {
  if (pollTimer) {
    pollTimer()
    pollTimer = null
  }
  if (typeof window !== 'undefined') {
    if (onlineHandler) {
      window.removeEventListener('online', onlineHandler)
      onlineHandler = null
    }
    if (offlineHandler) {
      window.removeEventListener('offline', offlineHandler)
      offlineHandler = null
    }
  }
  inflight = false
}

export const resetSyncWorkerForTests = (): void => {
  stopSyncWorker()
}
